import { GenerationSaleSubKind } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { PowerType } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import i18next from 'i18next'

import { type CivilDate, compareCivilDate } from '~/models/civilDate'

/**
 * 発電販売計画マスタの計画区分 (通常電源 / FIT 特例電源) フィルタ選択肢。
 * デフォルトは通常電源 (AC-03)。FIP は master では invalid (entity constructor
 * が reject する) ため候補に含めない (generation-sales-plan-category 設計書)。
 */
export const PLAN_CATEGORY_OPTIONS: GenerationSaleSubKind[] = [
  GenerationSaleSubKind.NORMAL,
  GenerationSaleSubKind.FIT,
]

/**
 * GenerationSaleSubKind → 日本語ラベル (F1 純関数)。
 * CSV エクスポート列 (AC-07)・一覧の計画区分列・削除確認ダイアログの文言 (AC-08)・
 * 作成/編集モーダルの disabled 表示 (AC-05 / AC-06) が全てこの関数を経由する。
 */
export function planCategoryLabel(value: GenerationSaleSubKind): string {
  const t = i18next.t.bind(i18next)
  switch (value) {
    case GenerationSaleSubKind.NORMAL:
      return t(
        'page:adminOcctoMaster.generationSalesPlanMaster.planCategory.normal',
      )
    case GenerationSaleSubKind.FIT:
      return t(
        'page:adminOcctoMaster.generationSalesPlanMaster.planCategory.fit',
      )
    default:
      return ''
  }
}

/**
 * 発電所 (系統コード) の電源種別が、計画区分の発電販売計画マスタに載せられるか。
 * 通常電源 = 非調整・調整・FIP 電源・FIT 特例②、FIT 特例電源 = FIT 特例①だけ。
 * 電源種別が決まっていない (未定) 発電所は、どの計画区分にも載せられない。
 * backend `valueobject.GenerationSaleSubKind.AcceptsPowerType` と同じ表 (mock `generationSalesScope`)。
 */
export function plantSuitsPlanCategory(
  powerType: PowerType | undefined,
  planCategory: GenerationSaleSubKind,
): boolean {
  switch (planCategory) {
    case GenerationSaleSubKind.NORMAL:
      return (
        powerType === PowerType.NON_ADJUSTABLE ||
        powerType === PowerType.ADJUSTABLE ||
        powerType === PowerType.FIP ||
        powerType === PowerType.FIT_EXCEPTION_2
      )
    case GenerationSaleSubKind.FIT:
      return powerType === PowerType.FIT_EXCEPTION_1
    default:
      return false
  }
}

/** 候補の判定に使うユニットの要点 (`PlantUnit` の一部。期間は有効開始日・有効終了日)。 */
export type PlantUnitForCandidate = {
  plantMasterId?: string
  powerType: PowerType
  validFrom?: { year: number; month: number; day: number }
  validTo?: { year: number; month: number; day: number }
}

/**
 * 発電販売計画マスタの発電所情報の候補に、この発電所 (系統コード) を出すか (電源ユニットマスタ 2b / AC-PUM-2B-9a、
 * DE 判断 2026-10-28 B・2026-10-31 Q1=A)。
 *
 * - 今日の電源種別が決まっていて計画区分に合う: 出す。
 * - それ以外 (今日は未定、または今日の種別が計画区分に合わない): **今日以降に有効な日があるユニット** (有効終了日が今日以降) の
 *   うち、計画区分に合う種別のものが 1 つでもあれば出す。例: 1 号機 (FIT 特例①、〜2027-03-31) と 2 号機 (FIP 電源、
 *   2027-04-01〜) の系統コードは、今日は FIT 特例①でも通常電源のマスタの候補に出し、4/1 からの行を登録できる。
 *   終了済みのユニットだけ・どの日も未定・先のユニットが計画区分に合わない系統コードは出さない。
 *
 * 行の期間で種別が決まるか・計画区分に合うか (E3・計画区分) は、保存時に backend が今日以降を日ごとに検査して止める。
 * backend `valueobject.GenerationSaleSubKind.AcceptsPowerType` と同じ表 (`plantSuitsPlanCategory`) を使う。
 */
export function plantIsCandidateForPlanCategory(input: {
  /** `ListPlantMasters` の今日時点の導出値 (未定 = undefined / UNSPECIFIED) */
  todayPowerType: PowerType | undefined
  plantMasterId: string
  units: readonly PlantUnitForCandidate[] | undefined
  planCategory: GenerationSaleSubKind
  today: CivilDate
}): boolean {
  const { todayPowerType, plantMasterId, units, planCategory, today } = input
  if (plantSuitsPlanCategory(todayPowerType, planCategory)) {
    return true
  }
  return (units ?? []).some((unit) => {
    if (unit.plantMasterId !== plantMasterId) {
      return false
    }
    const validTo =
      unit.validTo === undefined || unit.validTo.year === 0
        ? undefined
        : unit.validTo
    const notEnded =
      validTo === undefined || compareCivilDate(validTo, today) >= 0
    return notEnded && plantSuitsPlanCategory(unit.powerType, planCategory)
  })
}
